import { useEffect, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { Table, Button as AntButton, Pagination } from 'antd';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { getXperienceGoldenRecordsApi } from "../../../api's/DataProcessorApi";
import GoldenRecordDetails from './golden-records-components/GoldenRecordDetails';
import LoadingIcon from '../../../reusable-component/LoadingIcon';

function GoldenRecordsTab({ nodeId }) {
  const { enqueueSnackbar } = useSnackbar();

  const [headers, setHeaders] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5 });
  const [idFilterValue, setIdFilterValue] = useState('');

  const goldenRecordIdHeader = headers.find(h => h === 'golden_record_id');
  const otherHeaders = headers.filter(h => h !== 'golden_record_id');

  // Filter then slice ourselves so Table always gets exactly pageSize rows (avoids antd + duplicate-id bugs)
  const filteredRecords = useMemo(() => {
    if (!idFilterValue.trim()) return records;
    const q = idFilterValue.trim().toLowerCase();
    return records.filter((r) =>
      String(r.golden_record_id ?? '')
        .toLowerCase()
        .includes(q)
    );
  }, [records, idFilterValue]);

  const pageData = useMemo(() => {
    const { current, pageSize } = pagination;
    const start = (current - 1) * pageSize;
    const slice = filteredRecords.slice(start, start + pageSize);
    return slice.map((row, i) => ({ ...row, _rowKey: `p${pagination.current}-${start + i}` }));
  }, [filteredRecords, pagination]);

  const handleSelectRecordId = (recordId) => {
    setSelectedRecordId(recordId);
  };

  const getGoldenRecords = async () => {
    setLoading(true);
    try {
      const response = await getXperienceGoldenRecordsApi({ nodeId });
      // const response = mockGoldenRecordResult;

      if (response.status === 200) {
        const apiData = response.data;

        setHeaders(apiData.headers || []);

        // Use array index as unique key – API returns duplicate `id` values, which causes extra rows in antd Table
        const finalRows = (apiData.golden_records || []).map((row, index) => ({
          ...row,
          _rowKey: index,
        }));

        setRecords(finalRows);
      }
    } catch (error) {
      if (error.response?.status === 500) {
        enqueueSnackbar('500 Internal Server Error!', { variant: 'error' });
      } else if (error.response?.status === 404) {
        enqueueSnackbar('404 Golden Records not found!', { variant: 'error' });
      } else if (error.response?.status === 400) {
        enqueueSnackbar('Entity pipeline not executed yet!', { variant: 'warning' });
      } else {
        enqueueSnackbar('Something went wrong!', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getGoldenRecords();
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [nodeId]);

  const columns = [
    {
      title: 'VIEW',
      key: '__view__',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Tooltip title="View Golden Record" placement="top">
          <AntButton
            type="text"
            icon={
              <VisibilityOutlinedIcon
                fontSize="small"
                color="primary"
                sx={{ verticalAlign: 'middle' }}
              />
            }
            onClick={() => handleSelectRecordId(record.golden_record_id)}
          />
        </Tooltip>
      ),
    },

    //  golden_record_id column fixed after VIEW
    ...(goldenRecordIdHeader
      ? [{
        title: 'GOLDEN RECORD ID',
        dataIndex: 'golden_record_id',
        key: 'golden_record_id',
        align: 'center',
        width: 100,

        sorter: (a, b) =>
          String(a.golden_record_id)
            .localeCompare(String(b.golden_record_id), undefined, {
              numeric: true,
              sensitivity: 'base',
            }),

        sortDirections: ['ascend', 'descend'],

        render: (text) => {
          const textStr = String(text || '');
          const isLong = textStr.length > 15;
          const displayText = isLong ? `${textStr.substring(0, 15)}...` : textStr;

          return (
            <Tooltip title={isLong ? textStr : ''} placement="top">
              <span style={{ fontWeight: 'bold' }}>{displayText}</span>
            </Tooltip>
          );
        },


        filteredValue: idFilterValue ? [idFilterValue] : null,
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
          <Box sx={{ p: 1 }}>
            <input
              placeholder="Search ID"
              value={selectedKeys[0] ?? idFilterValue ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedKeys(v ? [v] : []);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirm();
              }}
              style={{
                width: 180,
                padding: 6,
                marginBottom: 8,
              }}
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <AntButton
                type="primary"
                size="small"
                onClick={() => {
                  setIdFilterValue(selectedKeys[0] ?? '');
                  setPagination((p) => ({ ...p, current: 1 }));
                  confirm();
                }}
              >
                Search
              </AntButton>

              <AntButton
                size="small"
                onClick={() => {
                  setIdFilterValue('');
                  setSelectedKeys([]);
                  setPagination((p) => ({ ...p, current: 1 }));
                  clearFilters();
                  confirm();
                }}
              >
                Reset
              </AntButton>
            </Box>
          </Box>
        ),
      }]
      : [])
    ,

    //  remaining dynamic headers
    ...otherHeaders.map((header) => ({
      title: header.replace(/_/g, ' ').toUpperCase(),
      dataIndex: header,
      key: header,
      align: 'center',
      width: 150,
    })),
  ];

  const handleToggleDetailPage = () => {
    setSelectedRecordId(null);
  };

  return (
    <Box sx={{ width: '55vw' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Golden Records
      </Typography>

      {selectedRecordId && (
        <GoldenRecordDetails handleToggleDetailPage={handleToggleDetailPage} goldenRecordId={selectedRecordId} nodeId={nodeId} />
      )}

      <Table
        key={`golden-${pagination.current}-${pagination.pageSize}`}
        columns={columns}
        dataSource={pageData}
        rowKey="_rowKey"
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
        <Pagination
          current={pagination.current}
          pageSize={pagination.pageSize}
          total={filteredRecords.length}
          showSizeChanger
          pageSizeOptions={[5, 10, 20]}
          showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
          onChange={(page, pageSize) => {
            const size = pageSize || 5;
            const maxPage = Math.max(1, Math.ceil(filteredRecords.length / size));
            setPagination({ current: Math.min(page, maxPage), pageSize: size });
          }}
        />
      </Box>

      {loading && <LoadingIcon />}
    </Box>
  );
}

export default GoldenRecordsTab;
